import { useNavigate } from "react-router-dom";

import { useOrphanDrafts } from "@/api/orphan-drafts";
import { useDispatchJob } from "@/api/runs";
import type { AuditLogEntry, JobStatus } from "@/api/types";

/** Props for {@link SendDraftsToValidationBanner}. */
export interface SendDraftsToValidationBannerProps {
  /** Canonical job UUID — passed through to the dispatch mutation
   * and to the {@link useOrphanDrafts} probe. */
  jobId: string;
  /** Terminal-status gate: only `"completed"` runs surface this
   * banner. Failed/cancelled runs go through Clear Drafts instead. */
  jobStatus: JobStatus;
  /** Agent-type gate: only `"db_curator"` writes `tools_draft`, so
   * the affordance is meaningless on any other agent's JobDetail. */
  agentType: string;
  /** Full `audit_log` list for the job — scanned for the
   * `worker_skipped_validator` `job_state_transition` reason that
   * distinguishes a targeted-mode opt-out from an autonomous-mode
   * validator success. */
  auditLog: readonly AuditLogEntry[];
}

/**
 * JobDetail affordance for the targeted-mode-completed trap (issue
 * #641). Post-#212 a targeted-mode Curator run terminates in
 * `completed` with the `worker_skipped_validator` state transition,
 * leaving unrated drafts in `tools_draft` with no discoverable path
 * forward from JobDetail — the sibling {@link ClearOrphanDraftsBanner}
 * only offers deletion.
 *
 * This banner is the missing "send them through validation"
 * affordance. Renders iff all four hold:
 *
 * 1. `jobStatus === "completed"` — the run reached terminal state.
 *    (Failed / cancelled runs go through Clear Drafts, not this.)
 * 2. `agentType === "db_curator"` — only the curator writes
 *    `tools_draft`; the affordance is meaningless elsewhere.
 * 3. Audit log contains a `job_state_transition` row with
 *    `reason === "worker_skipped_validator"` — the specific transition
 *    the targeted-mode curator writes when it opts out of the
 *    validator hop (see `services/orchestrator/src/routes/callbacks.ts`).
 * 4. The orphan-drafts probe returns `unratedSlugs.length > 0` — the
 *    server-side check that the Validator would actually have work
 *    to do. A subsequent validator catch-up that scored everything
 *    zeros this out and the banner correctly disappears.
 *
 * Clicking the button POSTs `{ agentType: "db_curator_validator",
 * context: {} }` via the shared {@link useDispatchJob} mutation —
 * exactly what the Manual Run → Validator (catch-up) card does — and
 * navigates to the new job's detail page on 202.
 *
 * Sits above `ClearOrphanDraftsBanner` in JobDetail's render order so
 * "progression" reads as the primary path and "abandon" as the
 * secondary. Both stay reachable — an operator who wants to clear
 * without validating can still do so from the banner below.
 */
export function SendDraftsToValidationBanner({
  jobId,
  jobStatus,
  agentType,
  auditLog,
}: SendDraftsToValidationBannerProps) {
  const navigate = useNavigate();
  const probe = useOrphanDrafts(jobId);
  const dispatch = useDispatchJob();

  // Client-side gate: cheap short-circuits first so the orphan-drafts
  // probe doesn't drive banner visibility on jobs that could never
  // qualify anyway (the probe still fires — the query hook is
  // unconditional — but its outcome is ignored here).
  if (jobStatus !== "completed") return null;
  if (agentType !== "db_curator") return null;
  if (!auditLogHasWorkerSkippedValidator(auditLog)) return null;

  // Server-side gate: the orphan-drafts probe carries the definitive
  // `unratedSlugs` list. Pending / errored probes render nothing so a
  // transient API failure doesn't surface a button that would 409
  // on click.
  const unratedCount = probe.data?.unratedSlugs.length ?? 0;
  if (unratedCount === 0) return null;

  const handleClick = async () => {
    try {
      const result = await dispatch.mutateAsync({
        agentType: "db_curator_validator",
        context: {},
      });
      navigate(`/jobs/${result.jobId}`);
    } catch {
      // Error surfaced via `dispatch.isError` below — no rethrow, the
      // mutation state drives the banner's error line so the operator
      // stays on-page for retry.
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded border border-accent/40 bg-accent-dim px-4 py-3">
      <div>
        <p className="text-sm text-text">
          {unratedCount} unrated draft {unratedCount === 1 ? "row" : "rows"}{" "}
          from this run are waiting in{" "}
          <span className="font-mono">tools_draft</span>.
        </p>
        <p className="text-xs text-text-muted break-all">
          Send them through the Validator (catch-up) to score them, then
          approve or reject in the review queue.
        </p>
        {dispatch.isError ? (
          <p className="text-xs text-red-300 font-mono break-all">
            Dispatch failed:{" "}
            {dispatch.error instanceof Error
              ? dispatch.error.message
              : String(dispatch.error)}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        disabled={dispatch.isPending}
        onClick={() => void handleClick()}
        className="inline-flex h-9 items-center justify-center rounded bg-accent px-4 text-sm font-medium text-bg hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {dispatch.isPending ? "Starting…" : "Send drafts through validation"}
      </button>
    </div>
  );
}

/**
 * Scans the audit trail for the specific `job_state_transition` row
 * the callback handler writes when a targeted-mode curator's
 * `output.expectsValidator === false` opts out of the validator hop.
 * See `services/orchestrator/src/routes/callbacks.ts` for the write
 * site and `docs/decisions/2026-06-08-targeted-curator-skips-validator.md`
 * for the design.
 *
 * Called from the banner's render path AND covered by dedicated unit
 * tests; exported both to make the gate directly testable and to
 * keep the render-time predicate expressible as a plain function.
 */
export function auditLogHasWorkerSkippedValidator(
  auditLog: readonly AuditLogEntry[],
): boolean {
  for (const entry of auditLog) {
    if (entry.eventType !== "job_state_transition") continue;
    const payload = entry.payload as { reason?: unknown } | null;
    if (payload && payload.reason === "worker_skipped_validator") return true;
  }
  return false;
}
